import React, { useEffect, useMemo, useRef, useState } from 'react';
import { trackUmami } from '../lib/umami';
import { Link } from 'react-router-dom';
import { getBookingOptions, getDeliveryQuote, submitProductOrder } from '../lib/api';
import { getCmsValue } from '../lib/siteContent';
import { buildFaqSchema, buildSectionFaq, useJsonLd } from '../lib/seo';

const NO_SITE_CONTENT = [];
const MIN_NAME_LENGTH = 2;
const PLACEHOLDER_LOGO = '/assets/smokehouse/harryda-bbq-logo-400.webp';
const PAGE_HEAD_LOGO = '/assets/smokehouse/logo-patch.jpg';

const CLOSED_MESSAGES = {
  deadline_passed: 'Beställningen är stängd för i år.',
  disabled: 'Beställningen är inte öppen just nu.',
};

// cryptoRef är injicerbar för test av fallbacken; i appen faller den alltid
// tillbaka till global crypto. Backend kräver en giltig UUID (pydantic
// uuid.UUID) – fallbacken måste därför bygga en riktig v4-UUID, inte bara en
// unik sträng, annars ger äldre webbläsare utan crypto.randomUUID en 422.
export function createRequestId(cryptoRef = typeof crypto !== 'undefined' ? crypto : undefined) {
  if (typeof cryptoRef?.randomUUID === 'function') {
    return cryptoRef.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof cryptoRef?.getRandomValues === 'function') {
    cryptoRef.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function formatSwedishDate(value, { weekday = false, year = true } = {}) {
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return date.toLocaleDateString('sv-SE', {
    ...(weekday ? { weekday: 'long' } : {}),
    day: 'numeric',
    month: 'long',
    ...(year ? { year: 'numeric' } : {}),
  });
}

const formatAmount = (value) => new Intl.NumberFormat('sv-SE').format(Math.round(Number(value)));

function formatKr(value) {
  return `${formatAmount(value)} kr`;
}

const formatKg = (value) => new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 2 }).format(Number(value));

// Viktvaror (Rökt julskinka) har pris per kg; antal = antal skinkor och
// beloppet är ett estimat (uppskattad vikt × kilopris) tills varan vägts.
const ORDER_GROUPS = [
  { id: 'boxes', title: 'Lådor' },
  { id: 'chark', title: 'Chark' },
];

/** Lådor = produkter med innehållslista; allt annat säljs som chark. */
const orderGroupOf = (product) => (
  Array.isArray(product.contents) && product.contents.length > 0 ? 'boxes' : 'chark'
);

const isWeightItem = (product) => product.is_weight_based === true;
const lineTotal = (product, quantity) => (isWeightItem(product)
  ? Number(product.estimated_weight_kg) * Number(product.price) * quantity
  : Number(product.price) * quantity);
const WEIGHT_ADJUSTMENT_NOTE = 'Slutsumma justeras efter vägning vid utlämning';

const ORDER_FORM_INITIAL = { name: '', email: '', phone: '', notes: '' };
const ADDRESS_INITIAL = { street: '', postal_code: '', city: '', note: '' };
const POSTAL_CODE_PATTERN = /^\d{5}$/;
const QUOTE_DELAY_MS = 400;
const QUOTE_ERROR = 'Avståndet kunde inte kontrolleras. Försök igen.';

function OrderFlow() {
  const [options, setOptions] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [quantities, setQuantities] = useState({});
  const [fulfillmentMethod, setFulfillmentMethod] = useState('pickup');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [address, setAddress] = useState(ADDRESS_INITIAL);
  // Avståndskontroll för hemkörning: { loading } medan den pågår, { error }
  // om anropet misslyckades, annars backendens { distance_mil, fee, allowed,
  // message }. Avgiften räknas om på servern vid beställning – men kunden ska
  // ha sett den innan Bekräfta går igenom.
  const [quote, setQuote] = useState(null);
  const [quoteAttempt, setQuoteAttempt] = useState(0);
  const [form, setForm] = useState(ORDER_FORM_INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [receiptItems, setReceiptItems] = useState([]);
  const requestIdRef = useRef(null);
  if (!requestIdRef.current) requestIdRef.current = createRequestId();
  // "Lägg till" byts mot stepper (och tillbaka vid 0) – flytta fokus till
  // den nya kontrollen så att tangentbordsanvändare inte tappar sin plats.
  const pendingFocusRef = useRef(null);
  const focusIfPending = (key) => (element) => {
    if (element && pendingFocusRef.current === key) {
      pendingFocusRef.current = null;
      element.focus();
    }
  };

  useEffect(() => {
    let active = true;
    getBookingOptions()
      .then((data) => { if (active) setOptions(data); })
      .catch(() => { if (active) setLoadError(true); });
    return () => { active = false; };
  }, []);

  const quotePostalCode = address.postal_code.replace(/\s/g, '');
  const quoteCity = address.city.trim();
  const canQuote = fulfillmentMethod === 'delivery' && POSTAL_CODE_PATTERN.test(quotePostalCode)
    && quoteCity.length >= 2;

  useEffect(() => {
    if (!canQuote) { setQuote(null); return undefined; }
    let active = true;
    setQuote({ loading: true });
    const timer = setTimeout(() => {
      getDeliveryQuote({ delivery_postal_code: quotePostalCode, delivery_city: quoteCity })
        .then((response) => { if (active) setQuote(response.error ? { error: true } : response.data); })
        .catch(() => { if (active) setQuote({ error: true }); });
    }, QUOTE_DELAY_MS);
    return () => { active = false; clearTimeout(timer); };
  }, [canQuote, quotePostalCode, quoteCity, quoteAttempt]);

  // Produkter utan pris visas inte, inte heller viktvaror utan uppskattad vikt.
  const products = (options?.products || []).filter((product) => Number(product.price) > 0
    && (!isWeightItem(product) || Number(product.estimated_weight_kg) > 0));
  const pickupDates = options?.pickup_dates || [];
  const deliveryDates = options?.delivery_dates || [];
  // Kunden väljer inte hämtdag – ordern läggs på första tillgängliga hämtdagen
  // och hämtas under någon av hämtdagarna (visas som info ovanför formuläret).
  const pickupDate = pickupDates.length > 0 ? String(pickupDates[0].date).slice(0, 10) : '';
  // Två grupper på beställningssidan: lådor (paket med innehåll) och chark
  // (styckvaror/viktvaror). Julskinkan är chark även om den har kategori jul.
  const productGroups = ORDER_GROUPS
    .map((group) => ({ ...group, products: products.filter((product) => orderGroupOf(product) === group.id) }))
    .filter((group) => group.products.length > 0);

  const setQuantity = (productId, quantity) => {
    setQuantities((current) => ({ ...current, [productId]: Math.max(0, quantity) }));
  };

  const changeQuantity = (productId, delta) => {
    const current = quantities[productId] || 0;
    const next = Math.max(0, current + delta);
    if (current === 0 && next > 0) {
      pendingFocusRef.current = `inc-${productId}`;
      trackUmami('order_add_to_cart');
    }
    if (current > 0 && next === 0) pendingFocusRef.current = `add-${productId}`;
    setQuantity(productId, next);
  };

  const items = products
    .map((product) => ({ product_id: product.id, name: product.name, price: product.price,
      quantity: Number(quantities[product.id]) || 0, is_weight_based: isWeightItem(product),
      line_total: lineTotal(product, Number(quantities[product.id]) || 0) }))
    .filter((item) => item.quantity > 0);
  const hasWeightItems = items.some((item) => item.is_weight_based);

  const quotedFee = quote && !quote.loading && !quote.error ? quote.fee : undefined;
  const deliveryFee = fulfillmentMethod !== 'delivery' ? 0
    : Number(quotedFee !== undefined ? quotedFee : options?.delivery_fee) || 0;
  let quoteText = '';
  if (quote?.loading) quoteText = 'Kontrollerar avstånd…';
  else if (quote?.error) quoteText = QUOTE_ERROR;
  else if (quote?.fee != null) quoteText = `Hemkörning ${formatKr(quote.fee)}`;
  else if (quote?.message) quoteText = quote.message;
  const total = items.reduce((sum, item) => sum + item.line_total, 0) + deliveryFee;

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleAddressChange = (event) => {
    const { name, value } = event.target;
    setAddress((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    if (items.length === 0) { setError('Välj minst en låda eller påse.'); return; }
    if (form.name.trim().length < MIN_NAME_LENGTH) { setError('Ange namn.'); return; }
    if (fulfillmentMethod === 'pickup' && !pickupDate) { setError('Inga hämtdagar är öppna just nu.'); return; }
    if (fulfillmentMethod === 'delivery') {
      if (!address.street.trim()) { setError('Ange en leveransadress.'); return; }
      if (!POSTAL_CODE_PATTERN.test(address.postal_code.replace(/\s/g, ''))) { setError('Ange ett postnummer med 5 siffror.'); return; }
      if (address.city.trim().length < 2) { setError('Ange ort.'); return; }
      if (!quote || quote.loading) { setError('Vänta tills avståndet är kontrollerat.'); return; }
      if (quote.error) { setError(QUOTE_ERROR); setQuoteAttempt((attempt) => attempt + 1); return; }
      if (quote.allowed === false) { setError(quote.message); return; }
      if (!deliveryDate) { setError('Välj en leveransdag.'); return; }
    }
    const isDelivery = fulfillmentMethod === 'delivery';

    // Snapshot av raderna vid klicktillfället – kunden kan ändra antal i
    // kundvagnen medan POST:en ligger ute, och kvittot ska visa det som
    // faktiskt skickades/sparades, inte vad korgen råkar innehålla när
    // svaret kommer tillbaka.
    const submittedItems = items;
    const itemCount = submittedItems.reduce((sum, item) => sum + item.quantity, 0);
    trackUmami('order_submit', { method: fulfillmentMethod, items: itemCount });
    setSubmitting(true);
    setError('');
    try {
      const response = await submitProductOrder({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        notes: form.notes,
        fulfillment_method: fulfillmentMethod,
        // pickup_date är utlämningsdagen: hämtdag eller leveransdag vid hemkörning.
        pickup_date: isDelivery ? deliveryDate : pickupDate,
        delivery_street: isDelivery ? address.street.trim() : null,
        delivery_postal_code: isDelivery ? address.postal_code.trim() : null,
        delivery_city: isDelivery ? address.city.trim() : null,
        delivery_note: isDelivery ? address.note.trim() : null,
        items: submittedItems.map(({ product_id, quantity }) => ({ product_id, quantity })),
        request_id: requestIdRef.current,
      });
      if (response.error) throw response.error;
      const order = response.data?.data;
      if (!order?.id) throw new Error('Bekräftelsen kunde inte läsas. Försök igen med samma uppgifter.');
      setReceiptItems(submittedItems);
      setReceipt(order);
      trackUmami('order_placed', {
        method: fulfillmentMethod,
        items: itemCount,
        revenue: Math.round(Number(order.total_amount) || 0),
        currency: 'SEK',
      });
    } catch (err) {
      trackUmami('order_failed', { method: fulfillmentMethod });
      setError(typeof err?.message === 'string' ? err.message : 'Beställningen kunde inte skickas. Försök igen.');
    } finally {
      setSubmitting(false);
    }
  };

  if (receipt) {
    // Ett omförsök med samma request_id får tillbaka det sparade kvittot utan
    // avgiftsuppgifterna – falla då tillbaka på avståndskontrollen kunden såg.
    const isDeliveryReceipt = receipt.fulfillment_method === 'delivery';
    const receiptFee = 'delivery_fee' in receipt ? receipt.delivery_fee : (isDeliveryReceipt ? quote?.fee : null);
    const receiptMessage = receipt.delivery_message
      || (isDeliveryReceipt && receiptFee == null ? quote?.message : null);
    const receiptEstimated = receipt.estimated_total === true || receiptItems.some((item) => item.is_weight_based);
    return (
      <section className="smoke-page smoke-jul-page" aria-live="polite">
        <div className="smoke-wrap">
          <div className="smoke-panel">
            <span className="smoke-kicker">Beställning mottagen</span>
            <h1>Tack för din beställning!</h1>
            <p className="smoke-jul-order-number">Bokningsnummer <strong>{receipt.order_number}</strong></p>
            <ul className="smoke-jul-contents">
              {receiptItems.map((item) => <li key={item.product_id}>{item.quantity} × {item.name}</li>)}
            </ul>
            <p>Totalt: <strong>{receiptEstimated ? 'ca ' : ''}{formatKr(receipt.total_amount)}</strong></p>
            {receiptEstimated && <p>{WEIGHT_ADJUSTMENT_NOTE}</p>}
            {receiptFee > 0 && <p>Hemkörning: {formatKr(receiptFee)}</p>}
            {receiptMessage && <p>{receiptMessage}</p>}
            {receipt.fulfillment_method === 'delivery'
              ? (
                <>
                  {receipt.pickup_date && <p>Leveransdag: {formatSwedishDate(receipt.pickup_date, { weekday: true })}</p>}
                  <p>Leveransadress:<br />
                    {/* Sparad som "Gata, 123 45 Ort[, portkod]" – visas radbruten. */}
                    {String(receipt.delivery_address).split(', ').map((line, index) => <React.Fragment key={index}>{line}<br /></React.Fragment>)}
                  </p>
                </>
              )
              : (
                <>
                  {options.pickup_place_name && <p>Hämtplats: {options.pickup_place_name}</p>}
                  {pickupDates.length > 0 && (
                    <p>Hämtdagar: {pickupDates.map((slot) => formatSwedishDate(slot.date, { weekday: true })).join(', ')}</p>
                  )}
                </>
              )}
            {options.payment_info && <p>{options.payment_info}</p>}
            <Link className="smoke-btn smoke-btn-secondary" to="/">Till startsidan</Link>
          </div>
        </div>
      </section>
    );
  }

  if (loadError) {
    return (
      <section className="smoke-page smoke-jul-page">
        <div className="smoke-wrap">
          <div className="smoke-panel">
            <h1>Jullådor &amp; chark</h1>
            <p role="alert">Kunde inte hämta bokningen just nu. Försök igen senare.</p>
          </div>
        </div>
      </section>
    );
  }

  if (!options) {
    return (
      <section className="smoke-page smoke-jul-page">
        <div className="smoke-wrap"><p role="status">Laddar…</p></div>
      </section>
    );
  }

  if (!options.open) {
    const message = options.closed_reason === 'fully_booked'
      ? (options.fully_booked_message || 'Tyvärr är julbeställningen fullbokad i år.')
      : (CLOSED_MESSAGES[options.closed_reason] || CLOSED_MESSAGES.disabled);
    return (
      <section className="smoke-page smoke-jul-page">
        <div className="smoke-wrap">
          <div className="smoke-panel">
            <span className="smoke-kicker">Beställ</span>
            <h1>Jullådor &amp; chark</h1>
            <p>{message}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="smoke-page smoke-jul-page">
      <div className="smoke-wrap">
        <div className="smoke-page-head smoke-jul-head">
          <div>
            <span className="smoke-kicker">Beställ</span>
            <h1>Jullådor &amp; chark</h1>
            <p className="smoke-lead">Chark från röken, samlat i färdiga lådor. Beställ i god tid till julbordet.</p>
            {(options.booking_deadline || pickupDates.length > 0) && (
              <p className="smoke-jul-dates">
                {options.booking_deadline && (
                  <span>Sista beställningsdag: <strong>{formatSwedishDate(options.booking_deadline)}</strong></span>
                )}
                {pickupDates.length > 0 && (
                  <span>Hämtdagar: <strong>{pickupDates.map((slot) => formatSwedishDate(slot.date, { weekday: true, year: false })).join(', ')}</strong></span>
                )}
              </p>
            )}
          </div>
          <img src={PAGE_HEAD_LOGO} alt="Härryda BBQ patch" />
        </div>

        <ul className="smoke-jul-trust">
          <li>Vi tillsätter inte nitritsalt eller andra tillsatser.</li>
          <li>Allt vårt kött kommer från Lammet &amp; Bonden.</li>
          <li>Baconet torrsaltas och har två bronsmedaljer från SM i Mathantverk.</li>
        </ul>

        <form onSubmit={handleSubmit} aria-label="Beställningsformulär">
          {productGroups.map((group) => (
          <section className="smoke-jul-group" key={group.id} aria-labelledby={`order-group-${group.id}`}>
          <h2 className="smoke-jul-group-title" id={`order-group-${group.id}`}>{group.title}</h2>
          <div className="smoke-products smoke-jul-products">
            {group.products.map((product) => {
              const quantity = quantities[product.id] || 0;
              return (
                <article className="smoke-product smoke-jul-box" key={product.id}>
                  <div className="smoke-product-image smoke-jul-box-image">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} />
                    ) : (
                      <div className="smoke-product-art smoke-jul-box-art" aria-hidden="true">
                        <img src={PLACEHOLDER_LOGO} alt="" />
                      </div>
                    )}
                  </div>
                  {product.summary && <span className="smoke-kicker">{product.summary}</span>}
                  <h3>{product.name}</h3>
                  {product.short_description && <p>{product.short_description}</p>}
                  {product.contents?.length > 0 && (
                    <ul className="smoke-jul-contents">
                      {product.contents.map((line) => <li key={line}>{line}</li>)}
                    </ul>
                  )}
                  <div className="smoke-jul-box-footer">
                    {isWeightItem(product) ? (
                      <p className="smoke-product-price">
                        <strong>{formatKr(product.price)}/kg</strong>
                        {product.weight_range_kg && (
                          <span className="smoke-jul-weight-range"> · ca {product.weight_range_kg.map(formatKg).join('–')} kg</span>
                        )}
                      </p>
                    ) : (
                      <p className="smoke-product-price"><strong>{formatKr(product.price)}</strong></p>
                    )}
                    {quantity === 0 ? (
                      <button
                        ref={focusIfPending(`add-${product.id}`)}
                        className="smoke-btn smoke-jul-add"
                        type="button"
                        aria-label={`Lägg till ${product.name}`}
                        onClick={() => changeQuantity(product.id, 1)}
                      >Lägg till</button>
                    ) : (
                      <div className="smoke-cart-qty">
                        <button
                          type="button"
                          aria-label={`Minska antal ${product.name}`}
                          onClick={() => changeQuantity(product.id, -1)}
                        >−</button>
                        <span aria-live="polite">{quantity}</span>
                        <button
                          ref={focusIfPending(`inc-${product.id}`)}
                          type="button"
                          aria-label={`Öka antal ${product.name}`}
                          onClick={() => changeQuantity(product.id, 1)}
                        >+</button>
                      </div>
                    )}
                  </div>
                  {isWeightItem(product) && product.weight_range_kg && (
                    <p className="smoke-jul-weight-note">
                      Slutpris efter vägning, ca {product.weight_range_kg.map((kg) => formatAmount(kg * product.price)).join('–')} kr/st
                    </p>
                  )}
                </article>
              );
            })}
          </div>
          </section>
          ))}

          <div className="smoke-cart smoke-jul-order">
            <span className="smoke-kicker">Beställning</span>

            <div className="smoke-jul-order-grid">
              <fieldset className="smoke-jul-fulfillment">
                <legend>Leveranssätt</legend>
                <div className="smoke-jul-choices">
                  <label className={`smoke-jul-choice${fulfillmentMethod === 'pickup' ? ' active' : ''}`}>
                    <input type="radio" name="fulfillment_method" value="pickup" aria-labelledby="jul-choice-pickup"
                      checked={fulfillmentMethod === 'pickup'} onChange={() => setFulfillmentMethod('pickup')} />
                    <span className="smoke-jul-choice-title" id="jul-choice-pickup">Upphämtning</span>
                    {fulfillmentMethod === 'pickup' && options.pickup_place_name && <span>Hämtplats: {options.pickup_place_name}</span>}
                    {fulfillmentMethod === 'pickup' && options.pickup_address && <span>{options.pickup_address}</span>}
                    {fulfillmentMethod === 'pickup' && options.pickup_times && <span>{options.pickup_times}</span>}
                    {fulfillmentMethod === 'pickup' && pickupDates.length > 0 && (
                      <span>Hämtas: {pickupDates.map((slot) => formatSwedishDate(slot.date, { weekday: true, year: false })).join(' eller ')}</span>
                    )}
                  </label>
                  {options.delivery_enabled && (
                    <label className={`smoke-jul-choice${fulfillmentMethod === 'delivery' ? ' active' : ''}`}>
                      <input type="radio" name="fulfillment_method" value="delivery" aria-labelledby="jul-choice-delivery"
                        checked={fulfillmentMethod === 'delivery'} onChange={() => setFulfillmentMethod('delivery')} />
                      <span className="smoke-jul-choice-title" id="jul-choice-delivery">Hemkörning</span>
                      {options.delivery_area && <span>Område: {options.delivery_area}</span>}
                      {options.delivery_fee != null && <span>Avgift: {formatKr(options.delivery_fee)}</span>}
                    </label>
                  )}
                </div>

                {fulfillmentMethod === 'delivery' && (
                  <div className="smoke-jul-address">
                    <label>Leveransdag *
                      <select value={deliveryDate} onChange={(event) => setDeliveryDate(event.target.value)} required>
                        <option value="">Välj leveransdag</option>
                        {deliveryDates.map((day) => (
                          <option key={day} value={day}>{formatSwedishDate(day, { weekday: true })}</option>
                        ))}
                      </select>
                    </label>
                    <label>Gatuadress *
                      <input name="street" value={address.street} onChange={handleAddressChange}
                        autoComplete="street-address" required />
                    </label>
                    <div className="smoke-form-row smoke-jul-postal">
                      <label>Postnummer *
                        <input name="postal_code" value={address.postal_code} onChange={handleAddressChange}
                          autoComplete="postal-code" inputMode="numeric" pattern="\d{3} ?\d{2}" maxLength={6} required />
                      </label>
                      <label>Ort *
                        <input name="city" value={address.city} onChange={handleAddressChange}
                          autoComplete="address-level2" minLength={2} required />
                      </label>
                    </div>
                    <label>Portkod/våning (valfritt)
                      <input name="note" value={address.note} onChange={handleAddressChange} />
                    </label>
                    {quoteText && (
                      <p className={`smoke-jul-quote${quote.allowed === false ? ' outside' : ''}`} role="status">{quoteText}</p>
                    )}
                  </div>
                )}
              </fieldset>

              <div className="smoke-jul-contact">
                <div className="smoke-form-row">
                  <label>Namn *
                    <input name="name" value={form.name} onChange={handleFormChange} autoComplete="name" required />
                  </label>
                  <label>Telefon *
                    <input type="tel" name="phone" value={form.phone} onChange={handleFormChange} autoComplete="tel" required />
                  </label>
                </div>
                <label>E-post *
                  <input type="email" name="email" value={form.email} onChange={handleFormChange} autoComplete="email" required />
                </label>
                <label>Meddelande (valfritt)
                  <textarea name="notes" value={form.notes} onChange={handleFormChange} />
                </label>
              </div>
            </div>

            {items.length > 0 && (
              <div className="smoke-jul-order-items">
                {items.map((item) => (
                  <div className="smoke-cart-item" key={item.product_id}>
                    <strong>{item.name}</strong>
                    <span>
                      {item.is_weight_based
                        ? `${item.quantity} × ${formatKr(item.price)}/kg · ca ${formatKr(item.line_total)}`
                        : `${item.quantity} × ${formatKr(item.price)}`}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {options.payment_info && <p className="smoke-jul-payment">{options.payment_info}</p>}
          </div>

          {/* Utanför panelen men inne i formuläret: på mobil blir raden sticky
              mot skärmens nederkant (så länge formuläret syns) när något är valt. */}
          <div className={`smoke-jul-submit${items.length > 0 ? ' has-items' : ''}`}>
            {error && <p className="smoke-form-status error" role="alert">{error}</p>}
            <div className="smoke-jul-submit-row">
              <p className="smoke-jul-total">
                Totalt: <strong>{hasWeightItems ? 'ca ' : ''}{formatKr(total)}</strong>
                {hasWeightItems && <span className="smoke-jul-total-note">{WEIGHT_ADJUSTMENT_NOTE}</span>}
              </p>
              <button className="smoke-btn" type="submit" disabled={submitting}>
                {submitting ? 'Skickar…' : 'Bekräfta beställning'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}

/** Sökordsbärande julintro och FAQ under beställningen; all text ur CMS-sektionen seo_order. */
function OrderSeoContent({ siteContent }) {
  const cms = (field) => getCmsValue(siteContent, 'seo_order', field);
  // FAQ-schemat byggs ur samma CMS-fält som renderas, så schema och sidtext matchar.
  const faqEntries = useMemo(() => buildSectionFaq(siteContent, 'seo_order'), [siteContent]);
  const faqSchema = useMemo(() => buildFaqSchema(faqEntries), [faqEntries]);
  useJsonLd('order-faq', faqSchema);

  return (
    <>
      {(cms('intro_title') || cms('intro_lead')) && (
        <section className="smoke-section" aria-labelledby="order-intro-heading">
          <div className="smoke-wrap">
            <h2 id="order-intro-heading">{cms('intro_title')}</h2>
            <p className="smoke-lead">{cms('intro_lead')}</p>
          </div>
        </section>
      )}
      {faqEntries.length > 0 && (
        <section className="smoke-section smoke-faq" aria-labelledby="order-faq-heading">
          <div className="smoke-wrap">
            <h2 id="order-faq-heading">{cms('faq_title')}</h2>
            <div className="smoke-faq-list">
              {faqEntries.map(({ slot, question, answer }) => (
                <details className="smoke-faq-item" key={slot}>
                  <summary>{question}</summary>
                  <p>{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}

export default function OrderPage({ siteContent = NO_SITE_CONTENT }) {
  return (
    <>
      <OrderFlow />
      <OrderSeoContent siteContent={siteContent} />
    </>
  );
}
